import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WriteReviewPage = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leadInfo, setLeadInfo] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [declared, setDeclared] = useState(false);

  const user = JSON.parse(localStorage.getItem("currentUser") || "null");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/reviews/lead/${leadId}`);
        setLeadInfo(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Impossibile caricare la richiesta");
      }
      setLoading(false);
    })();
  }, [leadId]);

  const submit = async () => {
    if (rating < 1) { setError("Seleziona almeno una stella"); return; }
    if (!declared) { setError("Devi dichiarare di aver effettivamente noleggiato prima di pubblicare"); return; }
    if (!user) { setError("Devi essere loggato come cliente per lasciare una recensione"); return; }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/reviews`, {
        lead_id: leadId,
        client_id: user.user_id || user.id,
        rating,
        comment,
      });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore durante l'invio della recensione");
    }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Caricamento...</div>;

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <div className="text-5xl mb-3">🎉</div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Grazie!</h1>
          <p className="text-slate-600 mb-6">La tua recensione è stata pubblicata e aiuterà altri clienti a scegliere.</p>
          <button onClick={() => navigate("/")} className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg">
            Torna alla home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <h1 className="text-2xl font-bold text-slate-800 mb-1" data-testid="write-review-title">Lascia la tua recensione</h1>
        {leadInfo?.company_name && (
          <p className="text-slate-500 text-sm mb-1">per <strong>{leadInfo.company_name}</strong></p>
        )}
        {leadInfo?.lead?.platform_title && (
          <p className="text-slate-400 text-xs mb-6">Piattaforma: {leadInfo.lead.platform_title}</p>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">Valutazione *</label>
          <div className="flex gap-1" data-testid="rating-input">
            {[1, 2, 3, 4, 5].map((i) => (
              <button
                key={i}
                type="button"
                onClick={() => setRating(i)}
                onMouseEnter={() => setHoverRating(i)}
                onMouseLeave={() => setHoverRating(0)}
                className="text-4xl transition-colors"
                data-testid={`star-${i}`}
              >
                <span className={(hoverRating || rating) >= i ? "text-yellow-500" : "text-slate-300"}>★</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">Commento (opzionale)</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Com'è andata l'esperienza? La piattaforma era come descritta? Il servizio dell'azienda è stato buono?"
            className="w-full h-32 border border-slate-300 rounded-lg p-3 text-slate-700 focus:outline-none focus:border-orange-500 resize-none text-sm"
            data-testid="review-comment-textarea"
          />
        </div>

        <div className="mb-6">
          <label className="flex items-start gap-3 cursor-pointer bg-amber-50 border border-amber-200 rounded-lg p-3 hover:bg-amber-100/50 transition-colors">
            <input
              type="checkbox"
              checked={declared}
              onChange={(e) => setDeclared(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-orange-600 cursor-pointer"
              data-testid="review-declaration-checkbox"
            />
            <span className="text-sm text-slate-700 leading-snug">
              Dichiaro di aver effettivamente concluso un rapporto di noleggio con l'azienda recensita e che la recensione pubblicata corrisponde alla mia esperienza reale.
            </span>
          </label>
        </div>

        {error && <p className="text-red-600 text-sm mb-4" data-testid="review-error">{error}</p>}

        <div className="flex justify-end gap-2">
          <button onClick={() => navigate("/")} className="px-4 py-2 text-slate-500 hover:bg-slate-100 rounded-lg">
            Annulla
          </button>
          <button
            onClick={submit}
            disabled={submitting || rating < 1 || !declared}
            className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="submit-review-btn"
          >
            {submitting ? "Invio..." : "Pubblica recensione"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WriteReviewPage;
